import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi } from
"@/components/ui/carousel";
import { industries } from "@/data/industries";
import { CarouselPagination } from "./CarouselPagination";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } }
};
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" as const } }
};

const IndustryCard = ({
  slug,
  title,
  benefit,
  bullets,
  roi,
  img,
  alt
}: (typeof industries)[number]) =>
<motion.div
  variants={cardVariants}
  whileHover={{ y: -6, boxShadow: "0 20px 40px rgba(0,0,0,0.18)" }}
  transition={{ type: "spring", stiffness: 380, damping: 22 }}
  className="h-full">
  
    <Card className="overflow-hidden rounded-md shadow-sm flex flex-col h-full">
      <img src={img} alt={alt} loading="lazy" className="h-40 w-full object-cover" />
      <CardHeader className="p-4">
        <CardTitle className="text-xl">{title}</CardTitle>
        <p className="text-base text-cream-muted">{benefit}</p>
      </CardHeader>
      <CardContent className="p-4 pt-0 flex-1 flex flex-col justify-between">
        <div>
          <ul className="list-disc pl-5 space-y-2 text-base text-cream-muted">
            {bullets.map((b) =>
          <li key={b}>{b}</li>
          )}
          </ul>
          <p className="mt-3 text-base text-cream-muted">{roi}</p>
        </div>
        <Button variant="default" className="mt-4 w-full" asChild>
          <Link to={`/industries/${slug}`} aria-label={`Learn more about ${title} automation solutions`}>
            Learn more about {title}
          </Link>
        </Button>
      </CardContent>
    </Card>
  </motion.div>;


const Industries = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const scrollToSlide = (index: number) => {
    if (api) {
      api.scrollTo(index);
    }
  };

  return (
    <section id="industries" className="py-16 md:py-24 grain-overlay bg-canvas">
      <div className="container mx-auto">
        <header className="mb-10 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold font-display">Industries I Help</h2>
          <p className="text-lg md:text-xl text-cream-muted mt-3">I work with small businesses across industries like these to cut overhead, run smarter, and focus on growth.

          </p>
        </header>

        {/* Mobile: enhanced carousel with peek */}
        <div className="md:hidden">
          <Carousel
            opts={{ align: "start", loop: false }}
            setApi={setApi}
            className="w-full">
            
            <CarouselContent className="-ml-2">
              {industries.map((ind, index) =>
              <CarouselItem
                key={ind.slug}
                className="pl-2 basis-[85%]"
                role="tabpanel"
                aria-label={`Slide ${index + 1} of ${industries.length}: ${ind.title}`}>
                
                  <IndustryCard {...ind} />
                </CarouselItem>
              )}
            </CarouselContent>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
          </Carousel>
          
          {/* Pagination */}
          <CarouselPagination
            currentSlide={current}
            totalSlides={industries.length}
            onDotClick={scrollToSlide} />
          
          
          {/* See all industries link */}
          <div className="text-center mt-6">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/industries" className="text-muted-foreground hover:text-foreground">
                See all industries →
              </Link>
            </Button>
          </div>
        </div>

        {/* Desktop: 4-column grid with stagger animation */}
        <motion.div
          className="hidden md:grid gap-8 sm:grid-cols-2 lg:grid-cols-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}>
          
          {industries.map((ind) =>
          <IndustryCard key={ind.slug} {...ind} />
          )}
        </motion.div>
      </div>
    </section>);

};

export default Industries;